import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import type * as THREE from 'three'
import manifest from './models.manifest.json'
import type { ModelEntry, RenderRecipe, ViewMode } from './lib/types'
import { parseWrapper } from './lib/parseWrapper'
import { SCENES, DEFAULT_SCENE } from './lib/scenes'
import placeholderSkinUrl from '../skins/placeholder-cover.png'
import { buildModel, setCoverTexture, type BuiltModel } from './lib/buildModel'
import { loadSkinTexture } from './lib/loadSkinTexture'
import { detectCapabilities } from './lib/capabilities'
import { CoverScene, type LightControls, type SceneApi, type Orientation } from './scene/CoverScene'
import { useHotkeys } from './scene/useHotkeys'
import { ViewControls, HelpOverlay } from './ui/ViewControls'
import { AdminPanel } from './ui/AdminPanel'
import { Loading, ErrorOverlay, UnavailableOverlay } from './ui/Loading'
import { Fallback2D } from './ui/Fallback2D'
import { ArOverlay } from './ui/ArOverlay'
import { QrOverlay } from './ui/QrOverlay'
import { exportBookGlb } from './lib/exportGlb'
import './ui/ui.css'

const MODELS = (manifest as { models: ModelEntry[] }).models
// Default cover when no ?skin= is given: the bundled placeholder, imported through
// Vite so it gets a content-hashed URL (no stale-cache surprises across deploys).
const DEFAULT_SKIN = placeholderSkinUrl

// Softcover spine thickness scales with page count (≈ this many pages = native model).
const SOFT_REF_PAGES = 120

function softcoverThickness(coverType: string, pages: number | null): number {
  if (coverType !== 'softcover' || pages == null) return 1
  return Math.min(3, Math.max(0.25, pages / SOFT_REF_PAGES))
}

// Ambient default brightened (0.72 → 1.08, +50%) per request.
const DEFAULT_LIGHT: LightControls = { ambient: 1.08, diffuse: 0.95, azimuthDeg: 25, elevationDeg: 35 }

// Per-cover-type orientation. The wrapper's modelRotation was authored in Chili's
// (Away3D) convention and does not translate to three, so we use an absolute pose
// per type (tuned against a golden model) that stands the front cover upright,
// facing +Z. Query overrides (?rx/ry/rz/order) replace these for further tuning.
// Whether the cover mesh's UVs are horizontally mirrored for this type (so text
// reads backwards). Tuned per type alongside ORIENTATION_BY_TYPE.
const FLIP_U_BY_TYPE: Record<string, boolean> = {
  hardcover: true,
  softcover: true,
  snapbook: true,
}

const ORIENTATION_BY_TYPE: Record<string, { order: THREE.EulerOrder; rotationDeg: [number, number, number] }> = {
  // Hardcover COLLADA is Z_UP; soft/snap are Y_UP, so they need the extra Y flip.
  // All three were tuned against a golden model to stand the front cover upright, facing +Z.
  hardcover: { order: 'ZYX', rotationDeg: [90, 0, 90] },
  softcover: { order: 'ZYX', rotationDeg: [90, 180, -90] },
  snapbook: { order: 'ZYX', rotationDeg: [90, 180, 90] },
}

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

const numParam = (p: URLSearchParams, name: string): number | null => {
  const v = p.get(name)
  if (v == null) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function readParams() {
  const p = new URLSearchParams(window.location.search)
  return {
    model: p.get('model'),
    skin: p.get('skin'),
    // Page count → auto-selects the matching hardcover model (?pages=120).
    pages: numParam(p, 'pages'),
    // Product cover type (?cover_type=SIGNATURE|CLASSIC|SOFTCOVER|SADDLE_STITCH).
    cover_type: p.get('cover_type'),
    // Trim size (?book_size=SIZE 8|SIZE 9). Defaults to SIZE 8.
    book_size: p.get('book_size'),
    // Legacy cover-type param (?type=softcover|snapbook), still honored.
    type: p.get('type'),
    mode: (p.get('mode') === 'admin' ? 'admin' : 'view') as ViewMode,
    bg: p.get('bg'),
    // Background scene preset (?scene=studio|slate|…) and/or a custom photo (?backdrop=<url>).
    scene: p.get('scene'),
    backdrop: p.get('backdrop'),
    spin: p.get('spin') !== '0',
    ar: p.get('ar'), // '1' forces the AR button on (e.g. for desktop testing)
    arv: p.get('arv'), // debug: override AR cover vertical flip ('0'/'1')
    arh: p.get('arh'), // debug: override AR cover horizontal flip ('0'/'1')
    // Orientation tuning overrides (degrees + Euler order).
    rx: numParam(p, 'rx'),
    ry: numParam(p, 'ry'),
    rz: numParam(p, 'rz'),
    order: p.get('order'),
    flipu: p.get('flipu'), // '1'/'0' to override the per-type horizontal texture flip
  }
}

/** Upper bound of a "20–60" style page range, or null. */
function rangeMax(pageRange: string | null): number | null {
  const nums = pageRange?.match(/\d+/g)
  return nums && nums.length ? Number(nums[nums.length - 1]) : null
}

type CoverType = ModelEntry['coverType']
type BookSize = 'SIZE 8' | 'SIZE 9'

// Product-facing cover_type values → internal cover type. SIGNATURE/CLASSIC are the
// two hardcover lines; SADDLE_STITCH is the snap book. Legacy lowercase values
// (softcover/snapbook/hardcover) also resolve. Anything unknown → hardcover.
const COVER_TYPE_PARAM: Record<string, CoverType> = {
  SIGNATURE: 'hardcover',
  CLASSIC: 'hardcover',
  HARDCOVER: 'hardcover',
  SOFTCOVER: 'softcover',
  SADDLE_STITCH: 'snapbook',
  SNAPBOOK: 'snapbook',
}

const COVER_TYPE_LABEL: Record<CoverType, string> = {
  hardcover: 'Hardcover',
  softcover: 'Softcover',
  snapbook: 'Snap Book',
}

function resolveCoverType(raw: string | null): CoverType {
  if (!raw) return 'hardcover'
  const key = raw.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return COVER_TYPE_PARAM[key] ?? 'hardcover'
}

/** SIZE 8 (default) or SIZE 9. Accepts "SIZE 9", "size 9", "9", etc. */
function resolveBookSize(raw: string | null): BookSize {
  return (raw ?? '').replace(/[^0-9]/g, '') === '9' ? 'SIZE 9' : 'SIZE 8'
}

/**
 * Resolve which model to show from the requested cover type + trim size (+ page count):
 *   - explicit ?model=<key> always wins (escape hatch / admin).
 *   - Hardcover: SIZE 9 → the "SIZE 9" model group; SIZE 8 → the standard top-level
 *     group. Within the group, ?pages picks the matching page-range mesh.
 *   - Softcover / Snap book: a single model each that renders at any book_size.
 * Returns null only if a requested combination has no model at all (defensive — the
 * app then shows a "3D model not available" card).
 */
function resolveEntry(
  params: ReturnType<typeof readParams>,
  coverType: CoverType,
  bookSize: BookSize,
): ModelEntry | null {
  if (params.model) {
    const slug = slugify(params.model)
    const found = MODELS.find((m) => m.key === slug)
    if (found) return found
  }

  if (coverType === 'hardcover') {
    // SIZE 9 lives in its own folder/group; SIZE 8 is the standard top-level set.
    const group = bookSize === 'SIZE 9' ? 'SIZE 9' : null
    const ranged = MODELS.filter(
      (m) => m.coverType === 'hardcover' && m.group === group && rangeMax(m.pageRange) != null,
    ).sort((a, b) => rangeMax(a.pageRange)! - rangeMax(b.pageRange)!)
    if (!ranged.length) return null
    if (params.pages != null) {
      // First range whose upper bound covers the page count (else the largest).
      return ranged.find((m) => rangeMax(m.pageRange)! >= params.pages!) ?? ranged.at(-1)!
    }
    return ranged[0]
  }

  // Softcover / snap book have a single model that renders at any requested size
  // (book_size doesn't change their mesh).
  return MODELS.find((m) => m.coverType === coverType && m.group == null) ?? null
}

/**
 * Accept any http(s) image URL (plus same-origin / blob / data); fall back to the
 * default placeholder for anything unparseable or a non-http protocol. The image
 * host must allow CORS so the bytes can be read into a texture.
 */
function resolveSkin(skinParam: string | null): string {
  if (!skinParam) return DEFAULT_SKIN
  if (skinParam.startsWith('/') || skinParam.startsWith('blob:') || skinParam.startsWith('data:')) {
    return skinParam
  }
  try {
    const url = new URL(skinParam, window.location.href)
    if (url.protocol === 'http:' || url.protocol === 'https:') return url.href
    console.warn(`Unsupported skin URL protocol: ${url.protocol} — using default skin.`)
  } catch {
    console.warn(`Invalid skin URL: ${skinParam} — using default skin.`)
  }
  return DEFAULT_SKIN
}

function modelUrl(path: string): string {
  return '/models/' + path.split('/').map(encodeURIComponent).join('/')
}

/** A custom backdrop is a CSS background-image (displayed, not read), so no CORS is needed. */
function sanitizeBackdrop(raw: string): string | null {
  if (raw.startsWith('data:image/')) return raw
  try {
    const u = new URL(raw, window.location.href)
    if (u.protocol === 'http:' || u.protocol === 'https:') return u.href
  } catch {
    /* invalid */
  }
  return null
}

/** Initial page background: ?backdrop= (image) → ?scene= (preset) → ?bg= (color) → default scene. */
function initialBackground(params: ReturnType<typeof readParams>): CSSProperties {
  if (params.backdrop) {
    const url = sanitizeBackdrop(params.backdrop)
    if (url) {
      return {
        backgroundImage: `url("${url}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    }
  }
  if (params.scene && SCENES[params.scene]) return { background: SCENES[params.scene].css }
  if (params.bg) return { background: `#${params.bg.replace(/^#/, '')}` }
  return { background: SCENES[DEFAULT_SCENE].css }
}

/** Load a skin, falling back to the default placeholder if the URL fails (bad URL, 404, or no CORS). */
async function loadSkinWithFallback(
  url: string,
  opts: Parameters<typeof loadSkinTexture>[1],
): Promise<THREE.Texture> {
  try {
    return await loadSkinTexture(url, opts)
  } catch (e) {
    if (url !== DEFAULT_SKIN) {
      console.warn(`Skin "${url}" failed to load (${e}); using default placeholder.`)
      return loadSkinTexture(DEFAULT_SKIN, opts)
    }
    throw e
  }
}

/** Per-type absolute orientation, with optional ?rx/ry/rz/order overrides for tuning. */
function orientation(coverType: string, params: ReturnType<typeof readParams>): Orientation {
  const t = ORIENTATION_BY_TYPE[coverType] ?? { order: 'XYZ' as THREE.EulerOrder, rotationDeg: [0, 0, 0] }
  return {
    rotationDeg: [
      params.rx ?? t.rotationDeg[0],
      params.ry ?? t.rotationDeg[1],
      params.rz ?? t.rotationDeg[2],
    ],
    order: (params.order ?? t.order) as THREE.EulerOrder,
  }
}

export function App() {
  const params = useMemo(readParams, [])
  const caps = useMemo(detectCapabilities, [])

  // Requested product cover type + trim size (drive model resolution and, when the
  // combination has no model, the "not available" message).
  const requestedCoverType = useMemo(() => resolveCoverType(params.cover_type ?? params.type), [params])
  const requestedBookSize = useMemo(() => resolveBookSize(params.book_size), [params])

  const [entry, setEntry] = useState<ModelEntry | null>(() =>
    resolveEntry(params, requestedCoverType, requestedBookSize),
  )
  const [skinUrl, setSkinUrl] = useState<string>(() => resolveSkin(params.skin))
  const [scene, setScene] = useState<{ recipe: RenderRecipe; group: THREE.Group } | null>(null)
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading')
  const [errorMsg, setErrorMsg] = useState('')
  const [light, setLight] = useState<LightControls>(DEFAULT_LIGHT)
  const [autoSpin, setAutoSpin] = useState(params.spin)
  const [showHelp, setShowHelp] = useState(false)
  const [showHint, setShowHint] = useState(true)

  const builtRef = useRef<BuiltModel | null>(null)
  const apiRef = useRef<SceneApi | null>(null)
  const skinUrlRef = useRef(skinUrl)
  skinUrlRef.current = skinUrl

  const texOpts = useCallback(
    (coverType: string, flipY: boolean) => ({
      maxSize: Math.min(caps.maxTextureSize || 2048, 2048),
      anisotropy: Math.min(caps.maxAnisotropy, 8),
      flipY,
      flipU: params.flipu != null ? params.flipu === '1' : (FLIP_U_BY_TYPE[coverType] ?? false),
    }),
    [caps, params.flipu],
  )

  // --- Load model (and apply the current skin) whenever the model changes ---
  useEffect(() => {
    if (!caps.webgl || !entry) return
    const e = entry
    let cancelled = false
    setPhase('loading')
    setErrorMsg('')
    ;(async () => {
      try {
        const buffer = await fetch(modelUrl(e.path)).then((r) => {
          if (!r.ok) throw new Error(`Failed to fetch model (${r.status})`)
          return r.arrayBuffer()
        })
        if (cancelled) return
        const recipe = parseWrapper(buffer)
        const built = buildModel(recipe, softcoverThickness(e.coverType, params.pages))
        // A cover that resolves by anything other than the exact COLLADA node means the
        // wrapper and the embedded COLLADA disagree — say so, loudly and with the mesh
        // names, so the next bad model export is a one-look diagnosis instead of a
        // customer reporting a blank preview.
        if (built.coverMeshCount === 0) {
          console.warn(
            `No cover mesh resolved for ${e.key} (looked for "${recipe.coverMeshName}"; ` +
              `model meshes: ${built.meshNames.filter(Boolean).join(', ') || 'none'})`,
          )
        } else if (built.matchedBy !== 'collada-node') {
          console.warn(
            `Cover mesh for ${e.key} resolved by the "${built.matchedBy}" fallback ` +
              `(wanted "${recipe.coverMeshName}") — check this model's wrapper/COLLADA.`,
          )
        }
        const texture = await loadSkinWithFallback(skinUrlRef.current, texOpts(e.coverType, e.flipY))
        if (cancelled) {
          built.dispose()
          texture.dispose()
          return
        }
        setCoverTexture(built, texture)
        builtRef.current?.dispose()
        builtRef.current = built
        setScene({ recipe, group: built.group })
        setPhase('ready')
      } catch (e) {
        if (!cancelled) {
          setErrorMsg(e instanceof Error ? e.message : String(e))
          setPhase('error')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [entry, caps, texOpts])

  // --- Re-apply skin when only the skin changes ---
  useEffect(() => {
    const built = builtRef.current
    if (!built || !entry) return // initial application is handled by the model effect
    const e = entry
    let cancelled = false
    ;(async () => {
      try {
        const texture = await loadSkinWithFallback(skinUrl, texOpts(e.coverType, e.flipY))
        if (cancelled) {
          texture.dispose()
          return
        }
        setCoverTexture(built, texture)
      } catch (err) {
        console.error('Failed to apply skin:', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [skinUrl, entry, texOpts])

  // Dispose GPU resources on unmount.
  useEffect(() => () => builtRef.current?.dispose(), [])

  // --- Hotkeys ---
  const hotkeyActions = useMemo(
    () => ({
      nudgeLightAzimuth: (d: number) =>
        setLight((l) => ({ ...l, azimuthDeg: ((l.azimuthDeg + d + 540) % 360) - 180 })),
      nudgeLightElevation: (d: number) =>
        setLight((l) => ({ ...l, elevationDeg: Math.max(-80, Math.min(80, l.elevationDeg + d)) })),
      toggleHelp: () => setShowHelp((s) => !s),
    }),
    [],
  )
  useHotkeys(apiRef, hotkeyActions, caps.webgl)

  const onUserInteract = useCallback(() => setShowHint(false), [])
  const onUploadSkin = useCallback((file: File) => setSkinUrl(URL.createObjectURL(file)), [])
  const onDownload = useCallback(() => {
    const data = apiRef.current?.screenshot()
    if (!data || !entry) return
    const a = document.createElement('a')
    a.href = data
    a.download = `cover-${entry.key}.png`
    a.click()
  }, [entry])

  // Page background ("scene") behind the transparent 3D canvas.
  const [sceneName, setSceneName] = useState(() =>
    params.scene && SCENES[params.scene] ? params.scene : DEFAULT_SCENE,
  )
  const [background, setBackground] = useState<CSSProperties>(() => initialBackground(params))
  const selectScene = useCallback((name: string) => {
    if (!SCENES[name]) return
    setSceneName(name)
    setBackground({ background: SCENES[name].css })
  }, [])

  // --- AR ("view in your space") ---
  const [arOpen, setArOpen] = useState(false)
  const [qrOpen, setQrOpen] = useState(false)
  const isMobile = useMemo(() => {
    const ua = navigator.userAgent
    return /iPhone|iPad|iPod|Android/.test(ua) || (/Mac/.test(ua) && navigator.maxTouchPoints > 1)
  }, [])
  // Mobile → in-page AR (model-viewer/Quick Look). Desktop → QR to open it on a phone.
  const showArButton = isMobile || params.ar === '1'
  const showQrButton = !isMobile
  // The QR points at COVER_PREVIEW_URL, carrying the current cover params (minus debug ones).
  const qrUrl = useMemo(() => {
    try {
      const u = new URL(__COVER_PREVIEW_URL__)
      const sp = new URLSearchParams(window.location.search)
      ;['ar', 'arv', 'arh'].forEach((k) => sp.delete(k))
      u.search = sp.toString()
      return u.href
    } catch {
      return __COVER_PREVIEW_URL__
    }
  }, [])
  const buildAr = useCallback(() => {
    if (!scene || !entry) return Promise.reject(new Error('Model not ready'))
    const o = orientation(entry.coverType, params)
    const flipU = params.flipu != null ? params.flipu === '1' : (FLIP_U_BY_TYPE[entry.coverType] ?? false)
    return exportBookGlb({
      recipe: scene.recipe,
      rotationDeg: o.rotationDeg,
      order: o.order,
      thickness: softcoverThickness(entry.coverType, params.pages),
      skinUrl,
      maxSize: Math.min(caps.maxTextureSize || 2048, 2048),
      flipV: params.arv != null ? params.arv === '1' : false,
      flipH: params.arh != null ? params.arh === '1' : flipU,
    })
  }, [scene, entry, skinUrl, caps.maxTextureSize])

  if (!caps.webgl) {
    return (
      <div className="app" style={background}>
        <Fallback2D src={skinUrl} />
      </div>
    )
  }

  return (
    <div className="app" style={background}>
      <div className="brand">Cover Preview</div>

      {entry == null ? (
        <UnavailableOverlay
          message={`A 3D model isn't available for ${COVER_TYPE_LABEL[requestedCoverType]} in ${requestedBookSize}.`}
        />
      ) : (
        <>
          {scene && (
            <CoverScene
              recipe={scene.recipe}
              group={scene.group}
              orientation={orientation(entry.coverType, params)}
              light={light}
              autoSpin={autoSpin}
              lowPower={caps.lowPower}
              apiRef={apiRef}
              onUserInteract={onUserInteract}
            />
          )}

          {phase === 'loading' && <Loading />}
          {phase === 'error' && <ErrorOverlay message={errorMsg} />}

          {phase === 'ready' && (
            <ViewControls
              apiRef={apiRef}
              autoSpin={autoSpin}
              onToggleSpin={() => setAutoSpin((s) => !s)}
              onDownload={onDownload}
              onToggleHelp={() => setShowHelp((s) => !s)}
              showHint={showHint}
              currentScene={sceneName}
              onSelectScene={selectScene}
              showAr={showArButton}
              onViewAr={() => setArOpen(true)}
              showQr={showQrButton}
              onShowQr={() => setQrOpen(true)}
            />
          )}
        </>
      )}

      {/* Admin panel stays available even on an unavailable combo, so staff can
          recover by picking a valid model from the dropdown. */}
      {params.mode === 'admin' && (
        <AdminPanel
          models={MODELS}
          currentModelKey={entry?.key ?? ''}
          onSelectModel={(key) => {
            const next = MODELS.find((m) => m.key === key)
            if (next) setEntry(next)
          }}
          onUploadSkin={onUploadSkin}
          light={light}
          onLightChange={(patch) => setLight((l) => ({ ...l, ...patch }))}
          onResetLight={() => setLight(DEFAULT_LIGHT)}
        />
      )}

      {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} />}
      {arOpen && scene && entry && <ArOverlay build={buildAr} onClose={() => setArOpen(false)} />}
      {qrOpen && <QrOverlay url={qrUrl} onClose={() => setQrOpen(false)} />}
    </div>
  )
}
